import { Component, Element, Prop, Watch, h } from '@stencil/core';

import { onQuestionAnswered } from '../actions/actions.answers';
import { actions as pageParamsActions } from '../actions/actions.page-params';
import { onQuestionsInit } from '../actions/actions.questions';
import historyActions from '../actions/actions.validation.vehicleHistory';
import { state as answerState, on as onAnswerStateChange } from '../store/store.answers';
import { state as historyState, onChange as onHistoryPropChange } from '../store/store.history';
import { on as onValidationStateChange, state as validationState } from '../store/store.validation';
import { trackVehicleHistoryLoad } from '../tracking/tracking.vehicleHistory';
import { QUESTION_ARE_QUESTIONS_VERIFIED, QUESTION_AUCTION_QUESTION_ANSWERED_BY_USER, QUESTION_VERIFIED_MILEAGE } from '../utils/utils.question';

@Component({
  tag: 'history-state-hydrator',
  shadow: true,
})
export class HistoryStateHydrator {
  @Element() el: HTMLElement;
  @Prop() colorId: number = 0;
  @Prop() drivetrainId: number = 0;
  @Prop() drivetrain: string = '';
  @Prop() engineId: number = 0;
  @Prop() makeId: number = 0;
  @Prop() mileage: number = 0;
  @Prop() modelId: number = 0;
  @Prop() transmissionId: number = 0;
  @Prop() trimId: number = 0;
  @Prop() vin: string = '';
  @Prop() yearId: number = 0;

  // Analytics
  @Prop() entry: string;
  @Prop() offerCode: string;
  @Prop() lnxCode: string;
  @Prop() category: string;
  @Prop() theme: string;

  @Prop() allowAutoAnswer: boolean = false;
  @Prop() showFormValidation: boolean = false;
  @Prop() allowIneligibleVehicleSubmission: boolean = false;
  @Prop() isAdmin: boolean = false;
  @Prop() alignLeft: boolean = false;

  @Watch('showFormValidation')
  onSetShowFormValidation(showFormValidation) {
    this.updateStateProps('showFormValidation', showFormValidation);
  }

  async componentWillLoad() {
    // boostrap props
    pageParamsActions.onReceivedParams({ entry: this.entry, offerCode: this.offerCode, lnxCode: this.lnxCode, category: this.category, isAdmin: this.isAdmin });

    const vehicle = {
      yearId: this.yearId,
      makeId: this.makeId,
      modelId: this.modelId,
      trimId: this.trimId,
      colorId: this.colorId,
      transmissionId: this.transmissionId,
      engineId: this.engineId,
      drivetrainId: this.drivetrainId,
      drivetrain: this.drivetrain,
      vin: this.vin,
      mileage: this.mileage,
    };

    // We don't actually need these props for our questions, but there's just the single API to call to get questions.
    // I assume once we integrate the all the pages we'll make a single call to get the questions and pass them around
    await onQuestionsInit(vehicle, this.allowIneligibleVehicleSubmission);

    // Call after init function to avoid questions override
    historyActions.onReceivedParams({ isAdmin: this.isAdmin });

    this.setupStateProps();

    onValidationStateChange('set', (prop, value) => {
      if (prop === 'fields') {
        this.updateStateProps('fieldErrors', value.vehicleHistory);
      }
      if (prop === 'form') {
        this.updateStateProps('formErrors', value.vehicleHistory);
      }
      if (prop === 'result') {
        this.updateStateProps('historyFormResult', value.vehicleHistory);
      }
    });

    onAnswerStateChange('set', (prop, value) => {
      this.updateStateProps(prop, value);
    });

    // Change this to be on question property. Otherwise we're getting multiple calls between the onChange for `questionsHistory` in the state and above call
    // If we only fire this on questions, it avoid that as the onChange for `questionsHistory` in the state is the only thing that should change `questions`.
    onHistoryPropChange('questions', (newValue) => {
      this.updateQuestionHistoryProp(newValue);
    });

    trackVehicleHistoryLoad();

    if (!this.allowAutoAnswer && !this.isAdmin) {
      // set answers if autocheck is not answering questions
      onQuestionAnswered(QUESTION_ARE_QUESTIONS_VERIFIED, 0);
      onQuestionAnswered(QUESTION_VERIFIED_MILEAGE, '');
      onQuestionAnswered(QUESTION_AUCTION_QUESTION_ANSWERED_BY_USER, 1);
    }
  }

  // updates only the changed prop
  updateStateProps(prop, value) {
    this.el.childNodes.forEach((element) => {
      element[prop] = value;
    });
  }
  // updates only the changed prop
  updateQuestionHistoryProp(newValue) {
    this.el.childNodes.forEach((element) => {
      element['questionsHistory'] = newValue;
    });
  }

  // explicitly declare initial state props
  setupStateProps() {
    this.el.childNodes.forEach((element) => {
      // History
      element['questionsHistory'] = historyState.questions;
      element['history'] = answerState.history;
      element['other'] = answerState.other;
      element['formErrors'] = validationState.form.vehicleHistory;
      element['fieldErrors'] = validationState.fields.vehicleHistory;
      element['historyFormResult'] = validationState.result.vehicleHistory;
      element['vin'] = this.vin;
      element['allowAutoAnswer'] = this.allowAutoAnswer;
      element['alignLeft'] = this.alignLeft;

      // theme
      element['theme'] = this.theme;
    });
  }

  render() {
    return (
      <div class="hydration-state hydration-state--conditions" data-testId="historyStateHydrator">
        <slot />
      </div>
    );
  }
}
